// src/pages/UserLogsPage.jsx
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Typography, Stack, Card, Avatar, TextField, InputAdornment,
  IconButton, CircularProgress, Tooltip, alpha, Chip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Pagination, Select, MenuItem, FormControl, InputLabel, Skeleton,
  Dialog, DialogContent, DialogTitle, Divider,
} from '@mui/material';
import {
  Search, Refresh, Close, Person, CalendarToday,
  CheckCircle, Cancel, FiberManualRecord, FilterList,
  AccessTime, Info,
} from '@mui/icons-material';
import { format, parseISO, isValid } from 'date-fns';
import { selectToken } from '../store/authSlice';

const API_BASE = 'https://workforce-api.govirtual.ph/api/v1';

// ── Utilities ─────────────────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return '—';
  try {
    const parsed = typeof d === 'string' ? parseISO(d) : new Date(d);
    return isValid(parsed) ? format(parsed, 'MMM d, yyyy') : '—';
  } catch { return '—'; }
}
function fmtDateTime(d) {
  if (!d) return '—';
  try {
    const parsed = typeof d === 'string' ? parseISO(d) : new Date(d);
    return isValid(parsed) ? format(parsed, 'MMM d, yyyy · h:mm a') : '—';
  } catch { return '—'; }
}
function fmtTimeAgo(d) {
  if (!d) return '—';
  try {
    const diff = Date.now() - new Date(d).getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)   return 'just now';
    if (mins < 60)  return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 30)  return `${days}d ago`;
    return fmtDate(d);
  } catch { return '—'; }
}

function fullName(u) {
  if (!u) return 'Unknown User';
  return [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || '—';
}
function initials(u) {
  if (!u) return '?';
  return [(u.firstName || '')[0], (u.lastName || '')[0]].filter(Boolean).join('').toUpperCase() || (u.username || '?')[0].toUpperCase();
}
const AVATAR_COLORS = ['#1075BA','#0a9e74','#7b1fa2','#c06000','#d32f2f','#0288d1','#558b2f','#6d4c41'];
function avatarColor(name = '') {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

// ── Status chip styling ───────────────────────────────────────────────────────
const STATUS_STYLES = {
  success:  { bg: '#e6f9f4', color: '#0a9e74', border: '#b3ead9', icon: <CheckCircle sx={{ fontSize: 11 }} /> },
  failed:   { bg: '#fff5f5', color: '#ef4444', border: '#fca5a5', icon: <Cancel sx={{ fontSize: 11 }} /> },
  error:    { bg: '#fff5f5', color: '#ef4444', border: '#fca5a5', icon: <Cancel sx={{ fontSize: 11 }} /> },
  pending:  { bg: '#fff4e0', color: '#c06000', border: '#ffd599', icon: <AccessTime sx={{ fontSize: 11 }} /> },
  default:  { bg: '#f0f4f8', color: '#4a6a85', border: '#c8d4dc', icon: <FiberManualRecord sx={{ fontSize: 11 }} /> },
};
function statusStyle(s) {
  if (!s) return STATUS_STYLES.default;
  const key = s.toLowerCase();
  return STATUS_STYLES[key] || STATUS_STYLES.default;
}

function StatusChip({ status }) {
  if (!status) return <Typography sx={{ color: '#c8d4dc', fontSize: '0.75rem' }}>—</Typography>;
  const st = statusStyle(status);
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 0.9, py: 0.3, borderRadius: '6px', fontSize: '0.68rem', fontWeight: 700, color: st.color, background: st.bg, border: `1px solid ${st.border}`, whiteSpace: 'nowrap' }}>
      {st.icon}{status}
    </Box>
  );
}

function ActionChip({ action }) {
  if (!action) return <Typography sx={{ color: '#c8d4dc', fontSize: '0.75rem' }}>—</Typography>;
  return (
    <Box sx={{ display: 'inline-block', px: 0.9, py: 0.3, borderRadius: '6px', fontSize: '0.68rem', fontWeight: 700, color: '#1075BA', background: '#e6f2ff', border: '1px solid #99ccf5', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
      {action}
    </Box>
  );
}

// ── Log detail modal ──────────────────────────────────────────────────────────
function LogDetailModal({ log, open, onClose }) {
  if (!log) return null;
  const user  = log.userId;
  const color = avatarColor(fullName(user));
  const rows = [
    { label: 'Log ID',     value: log.id || log._id, mono: true },
    { label: 'Action',     value: <ActionChip action={log.action} /> },
    { label: 'Status',     value: <StatusChip status={log.status} /> },
    { label: 'Reason',     value: log.reason || '—' },
    { label: 'Created',    value: fmtDateTime(log.createdAt) },
    { label: 'Updated',    value: fmtDateTime(log.updatedAt) },
  ];
  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}
      PaperProps={{ sx: { width: { xs: '98vw', md: '520px' }, borderRadius: '20px', overflow: 'hidden' } }}>
      <DialogTitle sx={{ px: 3, py: 2, borderBottom: '1px solid rgba(16,117,186,0.08)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 34, height: 34, borderRadius: '9px', background: '#e6f2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Info sx={{ fontSize: 17, color: '#1075BA' }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f2137', flex: 1 }}>Log Entry</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: '#9aacb8' }}><Close fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {/* User banner */}
        {user && (
          <Box sx={{ px: 3, py: 2, background: alpha(color, 0.05), borderBottom: '1px solid rgba(16,117,186,0.07)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar src={user.photoURL}
              sx={{ width: 42, height: 42, fontSize: '0.9rem', fontWeight: 700, background: color, flexShrink: 0 }}>
              {initials(user)}
            </Avatar>
            <Box>
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#0f2137' }}>{fullName(user)}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#4a6a85' }}>@{user.username || '—'}</Typography>
            </Box>
          </Box>
        )}
        {/* Detail rows */}
        <Box sx={{ px: 3, py: 2 }}>
          <Stack spacing={0}>
            {rows.map(({ label, value, mono }, i) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, py: 1.1, borderBottom: i < rows.length - 1 ? '1px solid rgba(16,117,186,0.06)' : 'none' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#4a6a85', minWidth: 80, mt: 0.1 }}>{label}</Typography>
                {typeof value === 'string'
                  ? <Typography sx={{ fontSize: '0.82rem', color: '#0f2137', fontWeight: 500, flex: 1, fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{value}</Typography>
                  : <Box sx={{ flex: 1 }}>{value}</Box>
                }
              </Box>
            ))}
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ── Table skeleton ────────────────────────────────────────────────────────────
function TableSkeleton({ rows = 10 }) {
  return Array.from({ length: rows }).map((_, i) => (
    <TableRow key={i}>
      <TableCell><Skeleton variant="rounded" height={16} width={80} /></TableCell>
      <TableCell>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Skeleton variant="circular" width={32} height={32} />
          <Box><Skeleton variant="text" width={110} height={14} /><Skeleton variant="text" width={70} height={12} /></Box>
        </Stack>
      </TableCell>
      <TableCell><Skeleton variant="rounded" height={22} width={80} sx={{ borderRadius: '6px' }} /></TableCell>
      <TableCell><Skeleton variant="rounded" height={22} width={64} sx={{ borderRadius: '6px' }} /></TableCell>
      <TableCell><Skeleton variant="text" width={120} /></TableCell>
      <TableCell><Skeleton variant="text" width={60} /></TableCell>
    </TableRow>
  ));
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function UserLogsPage() {
  const token = useSelector(selectToken);

  const [rows,       setRows]       = useState([]);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(false);

  const [search,     setSearch]     = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dateFrom,   setDateFrom]   = useState('');
  const [dateTo,     setDateTo]     = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [pageSize,   setPageSize]   = useState(30);

  const [actions,    setActions]    = useState([]);  // distinct action values
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalOpen,  setModalOpen]  = useState(false);

  const searchTimer = useRef(null);

  const handleSearchChange = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setDebouncedSearch(val); setPage(1); }, 400);
  };

  // ── Fetch distinct actions for filter dropdown ─────────────────
  useEffect(() => {
    fetch(`${API_BASE}/userlog/actions`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => Array.isArray(d) ? setActions(d) : null)
      .catch(() => {});
  }, [token]);

  // ── Fetch logs ─────────────────────────────────────────────────
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, size: pageSize });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (dateFrom)        params.set('from',   dateFrom);
      if (dateTo)          params.set('to',     dateTo);
      if (statusFilter)    params.set('status', statusFilter);
      if (actionFilter)    params.set('action', actionFilter);

      const res  = await fetch(`${API_BASE}/userlog?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const r    = data.rows || (Array.isArray(data) ? data : []);
      setRows(r);
      setTotal(data.total || r.length);
      setTotalPages(data.lastPage || 1);
    } catch (e) { console.error('fetch logs', e); }
    setLoading(false);
  }, [token, page, pageSize, debouncedSearch, dateFrom, dateTo, statusFilter, actionFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const clearFilters = () => {
    setSearch(''); setDebouncedSearch('');
    setDateFrom(''); setDateTo('');
    setStatusFilter(''); setActionFilter('');
    setPage(1);
  };

  const hasActiveFilters = debouncedSearch || dateFrom || dateTo || statusFilter || actionFilter;

  const openLog = (log) => { setSelectedLog(log); setModalOpen(true); };

  return (
        <Box sx={{ height: 'calc(94vh)', display: 'flex', flexDirection: 'column', gap: 2 }}>


      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f2137', mb: 0.25 }}>User Logs</Typography>
          <Typography variant="body2" sx={{ color: '#4a6a85' }}>
            Audit trail of all user actions and system events
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75, borderRadius: '10px', background: '#e6f2ff', border: '1px solid #99ccf5' }}>
            <FiberManualRecord sx={{ fontSize: 8, color: '#1075BA' }} />
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1075BA' }}>
              {total.toLocaleString()} {total === 1 ? 'Entry' : 'Entries'}
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchLogs} disabled={loading}
              sx={{ color: '#1075BA', background: alpha('#1075BA', 0.08), border: `1px solid ${alpha('#1075BA', 0.2)}`, borderRadius: '10px', width: 36, height: 36 }}>
              {loading ? <CircularProgress size={16} sx={{ color: '#1075BA' }} /> : <Refresh fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* ── Filters ── */}
      <Card sx={{ p: 2, border: '1px solid rgba(16,117,186,0.1)', boxShadow: '0 2px 8px rgba(15,33,55,0.04)', borderRadius: '14px' }}>
        <Stack spacing={1.5}>
          {/* Row 1: search + status + action + page size */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
            <TextField
              size="small" placeholder="Search by user name or username…"
              value={search} onChange={(e) => handleSearchChange(e.target.value)}
              sx={{ flex: 1 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ color: '#4a6a85', fontSize: 18 }} /></InputAdornment>,
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => handleSearchChange('')}><Close sx={{ fontSize: 15 }} /></IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            {/* Status filter */}
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} label="Status">
                <MenuItem value="">All statuses</MenuItem>
                {['success','failed','error','pending'].map((s) => (
                  <MenuItem key={s} value={s}><StatusChip status={s} /></MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Action filter */}
            {actions.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Action</InputLabel>
                <Select value={actionFilter} onChange={(e) => { setActionFilter(e.target.value); setPage(1); }} label="Action">
                  <MenuItem value="">All actions</MenuItem>
                  {actions.map((a) => (
                    <MenuItem key={a} value={a} sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{a}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Per page</InputLabel>
              <Select value={pageSize} onChange={(e) => { setPageSize(e.target.value); setPage(1); }} label="Per page">
                {[20, 30, 50, 100].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>

          {/* Row 2: date range */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
              <CalendarToday sx={{ fontSize: 15, color: '#4a6a85' }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#4a6a85' }}>Date range</Typography>
            </Box>
            <TextField
              size="small" type="date" label="From"
              value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: dateTo || undefined }}
              sx={{ flex: 1 }}
            />
            <Typography sx={{ color: '#9aacb8', fontSize: '0.8rem', flexShrink: 0 }}>to</Typography>
            <TextField
              size="small" type="date" label="To"
              value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: dateFrom || undefined }}
              sx={{ flex: 1 }}
            />
            {hasActiveFilters && (
              <Button size="small" startIcon={<FilterList sx={{ fontSize: 15 }} />} onClick={clearFilters}
                sx={{ flexShrink: 0, borderRadius: '9px', textTransform: 'none', fontSize: '0.78rem', fontWeight: 600, color: '#4a6a85', border: '1px solid #e5e7eb', px: 1.5, '&:hover': { background: '#f5f8fc' } }}>
                Clear filters
              </Button>
            )}
          </Stack>
        </Stack>
      </Card>

      {/* ── Table ── */}
      <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid rgba(16,117,186,0.08)', boxShadow: '0 2px 8px rgba(15,33,55,0.04)' }}>
        <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {['Timestamp', 'User', 'Action', 'Status', 'Reason', ''].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', whiteSpace: 'nowrap', py: 1.5, background: '#f5f8fc', color: '#4a6a85' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableSkeleton rows={pageSize > 20 ? 12 : 8} />
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                    <Stack alignItems="center" spacing={1.5}>
                      <Box sx={{ width: 56, height: 56, borderRadius: '16px', background: '#f0f4f8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FilterList sx={{ fontSize: 28, color: '#c8d4dc' }} />
                      </Box>
                      <Typography sx={{ color: '#6b7f8e', fontSize: '0.92rem', fontWeight: 600 }}>No log entries found</Typography>
                      <Typography sx={{ color: '#9aacb8', fontSize: '0.8rem' }}>
                        {hasActiveFilters ? 'Try adjusting your filters' : 'No logs have been recorded yet'}
                      </Typography>
                      {hasActiveFilters && (
                        <Button size="small" onClick={clearFilters}
                          sx={{ borderRadius: '9px', textTransform: 'none', fontWeight: 600, color: '#1075BA' }}>
                          Clear all filters
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : rows.map((log) => {
                const user  = log.userId;
                const color = avatarColor(fullName(user));
                return (
                  <TableRow key={log.id || log._id} hover
                    onClick={() => openLog(log)}
                    sx={{ cursor: 'pointer', '&:hover': { background: alpha('#1075BA', 0.035) }, transition: 'background 0.1s' }}>

                    {/* Timestamp */}
                    <TableCell sx={{ minWidth: 160, whiteSpace: 'nowrap' }}>
                      <Typography sx={{ fontSize: '0.78rem', color: '#0f2137', fontWeight: 500 }}>
                        {fmtDate(log.createdAt)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: '#9aacb8' }}>
                        {fmtTimeAgo(log.createdAt)}
                      </Typography>
                    </TableCell>

                    {/* User */}
                    <TableCell sx={{ minWidth: 200 }}>
                      {user ? (
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                          <Avatar src={user.photoURL}
                            sx={{ width: 32, height: 32, fontSize: '0.72rem', fontWeight: 700, background: color, flexShrink: 0 }}>
                            {initials(user)}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#0f2137', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {fullName(user)}
                            </Typography>
                            <Typography sx={{ fontSize: '0.68rem', color: '#6b7f8e' }}>@{user.username || '—'}</Typography>
                          </Box>
                        </Stack>
                      ) : (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ width: 32, height: 32, borderRadius: '50%', background: '#f0f4f8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Person sx={{ fontSize: 16, color: '#c8d4dc' }} />
                          </Box>
                          <Typography sx={{ fontSize: '0.78rem', color: '#9aacb8', fontFamily: 'monospace' }}>
                            {typeof log.userId === 'string' ? log.userId : '—'}
                          </Typography>
                        </Stack>
                      )}
                    </TableCell>

                    {/* Action */}
                    <TableCell sx={{ minWidth: 140 }}>
                      <ActionChip action={log.action} />
                    </TableCell>

                    {/* Status */}
                    <TableCell sx={{ minWidth: 100 }}>
                      <StatusChip status={log.status} />
                    </TableCell>

                    {/* Reason */}
                    <TableCell sx={{ minWidth: 200, maxWidth: 320 }}>
                      <Typography sx={{ fontSize: '0.78rem', color: '#4a6a85', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>
                        {log.reason || <span style={{ color: '#c8d4dc' }}>—</span>}
                      </Typography>
                    </TableCell>

                    {/* Detail arrow */}
                    <TableCell sx={{ width: 40 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#c8d4dc' }}>›</Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Pagination footer ── */}
        <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid rgba(16,117,186,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, background: '#fff' }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#4a6a85' }}>
            {loading ? (
              <Skeleton variant="text" width={160} />
            ) : (
              <>Showing <strong>{rows.length}</strong> of <strong>{total.toLocaleString()}</strong> entries — page <strong>{page}</strong> of <strong>{totalPages}</strong></>
            )}
          </Typography>
          <Pagination
            count={totalPages} page={page}
            onChange={(_, p) => setPage(p)}
            size="small" color="primary"
            sx={{ '& .MuiPaginationItem-root': { fontSize: '0.78rem' } }}
          />
        </Box>
      </Card>

      {/* ── Log detail modal ── */}
      <LogDetailModal log={selectedLog} open={modalOpen} onClose={() => setModalOpen(false)} />
    </Box>
  );
}
